<template>
  <main id="maincontent">
    <div class="global-component content-wrapper">
      <div class="search-results-wrapper">
        <form class="search-bar" role="search" aria-label="Søgning" @submit.prevent="fetchData">
          <label class="search-input-wrapper">
            <span class="visually-hidden">Søg</span>
            <input name="Søg" type="search" class="style-small" :placeholder="searchPlaceholderLabel" v-model="searchString"/>
            <div class="search-buttons">
              <button class="search-button" :class="{ 'disabled': !searchString || searchString.trim() === '' }"
              type="submit" aria-label="Søg">
                  <span class="icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.6 18L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13C4.68333 13 3.14583 12.3708 1.8875 11.1125C0.629167 9.85417 0 8.31667 0 6.5C0 4.68333 0.629167 3.14583 1.8875 1.8875C3.14583 0.629167 4.68333 0 6.5 0C8.31667 0 9.85417 0.629167 11.1125 1.8875C12.3708 3.14583 13 4.68333 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L18 16.6L16.6 18ZM6.5 11C7.75 11 8.8125 10.5625 9.6875 9.6875C10.5625 8.8125 11 7.75 11 6.5C11 5.25 10.5625 4.1875 9.6875 3.3125C8.8125 2.4375 7.75 2 6.5 2C5.25 2 4.1875 2.4375 3.3125 3.3125C2.4375 4.1875 2 5.25 2 6.5C2 7.75 2.4375 8.8125 3.3125 9.6875C4.1875 10.5625 5.25 11 6.5 11Z" fill="currentColor"></path>
                    </svg>
                  </span>
              </button>
            </div>
          </label>
        </form>
        <div class="search-options" role="radiogroup" aria-label="Søg filters">
            <label :class="{ active: resultType === 'all' }" class="style-single-line" for="search-all-button">{{searchAllLabel}}</label>
            <input type="radio" value="all" id="search-all-button" v-model="resultType"/>
            <div class="separator"></div>
            <label :class="{ active: resultType === 'rules' }" class="style-single-line" for="search-rules-button">{{searchRulesLabel}}</label>
            <input type="radio" value="rules" id="search-rules-button" v-model="resultType"/>
          </div>

        <div class="filters-wrapper" v-if="resultType === 'rules'">
          <Dropdown
            v-model="selectedIndustryTagValues"
            :tags="industryTags.map(t => t.Value)"
            dropdownLabel="Branche"
          />
          <Dropdown
            v-model="selectedRuleTagValues"
            :tags="ruleTags.map(t => t.Value)"  
            dropdownLabel="Regler"
          />

          <MobileFilters
            v-if="filters.length"
            :filters="filters"
            :is-open="mobileFiltersOpen"
            :total-visible="data?.Documents?.length || 0"
            :total-results="data?.TotalResults || 0"
            :any-filter-set="anyFilterSet"
            @close="toggleMobileFilters"
            @clear="resetSearch"
          />
        </div>

        <button v-if="resultType === 'rules'" class="button-secondary filter-btn" @click="toggleMobileFilters">
            <span class="filter-text">Filtre</span>
            <svg class="icon icon-filter" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8.40156 2.39961L8.40156 6.39961H7.20156V4.99961L2.40156 4.99961V3.79961L7.20156 3.79961L7.20156 2.39961H8.40156ZM13.6016 3.79961V4.99961H9.60156V3.79961H13.6016ZM11.2016 5.99961V7.39961H13.6016V8.59961H11.2016V9.99961H10.0016V5.99961H11.2016ZM8.80156 7.39961V8.59961L2.40156 8.59961V7.39961L8.80156 7.39961ZM6.40156 9.59961L6.40156 13.5996L5.20156 13.5996V12.1996H2.40156V10.9996L5.20156 10.9996V9.59961H6.40156ZM13.6016 10.9996V12.1996H7.20156V10.9996H13.6016Z" fill="currentColor"></path>
            </svg>
          </button>

        <div v-if="data === null">
          <div class="spinner"></div>
        </div>

        <div v-if="data !== null">
          <search-results :data="data"
                          :selected-industry-tags="selectedIndustryTags"
                          :selected-rule-tags="selectedRuleTags"
                          :banner="banner"
                          :is-rules-search="resultType === 'rules'"
                          :search-label="searchLabel"
                          :search-id="searchId"
                          @fetch-more-content="fetchMoreContent"
                          @reset-search="resetSearch">
          </search-results>
        </div>
      </div>
    </div>
  </main>
</template>


<script setup>
import {onMounted, watch, ref, computed} from "vue";
import SearchResults from './searchResults.vue';
import Dropdown from '../dropdown.vue';
import MobileFilters from '../mobileFilters.vue';
import { initHoverArrowAnimation } from '../../../layout-handlers.js'

const data = ref(null)
const pageSize = ref(50)
// start as empty so the input is blank; '*' is used only for requests/URL
const searchString = ref('')
const resultType = ref('all')
const industryTags = ref([])
const ruleTags = ref([])
const banner = ref(null)
const searchId = ref(null)
const selectedIndustryTags = ref([])
const selectedRuleTags = ref([])
const mobileFiltersOpen = ref(false)
const currentUrl = ref('')

const props = defineProps({
  searchUrl: {
    type: String,
    required: true
  },
  rulesUrl: {
    type: String,
    required: true
  },
  searchPlaceholderLabel: {
    type: String,
    required: true
  },
  searchAllLabel: {
    type: String,
    required: true
  },
  searchRulesLabel: {
    type: String,
    required: true
  }});

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('q');
  const type = params.get('type');

  // only set searchString from query if it's a real query (not the wildcard '*')
  if (query && query !== '*') {
    searchString.value = query;
  }

  if (type && (type === 'all' || type === 'rules')) {
    resultType.value = type;
  }

  if(resultType.value === 'rules') {
    currentUrl.value = props.rulesUrl
  }
  else  {
    currentUrl.value = props.searchUrl
  }

  const tagMapping = {
    IndustryTags: selectedIndustryTags,
    ruleTags: selectedRuleTags,
  };

  Object.entries(tagMapping).forEach(([key, reactiveTags]) => {
    const tagValues = params.getAll(key);
    reactiveTags.value = tagValues.map(val => ({Value: val}));
  });

  await fetchData()

})

const fetchMoreContent = async () => {
  pageSize.value += 50
  await fetchData();
  initHoverArrowAnimation();
}

const updateURL = () => {
  const currentURL = new URL(window.location.href);
  // write '*' to URL when the input is empty so the backend still gets the wildcard
  const qForUrl = searchString.value.toString().trim() === '' ? '*' : searchString.value;
  currentURL.searchParams.set('q', qForUrl);
  currentURL.searchParams.set('type', resultType.value);

  const tagGroups = {
    IndustryTags: selectedIndustryTags.value,
    ruleTags: selectedRuleTags.value,
  };

  Object.entries(tagGroups).forEach(([key, value]) => {
    currentURL.searchParams.delete(key);
    value.forEach(tag => {
      currentURL.searchParams.append(key, tag.Value)
    })
  })

  window.history.pushState(null, '', currentURL);
};

const toggleMobileFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value;
};

function closeMobileFilters() {
  mobileFiltersOpen.value = false;
}

const resetSearch = async () => {
  closeMobileFilters();
  selectedIndustryTags.value = [];
  selectedRuleTags.value = [];
  resultType.value = 'all';
  searchString.value = '';

  currentUrl.value = props.searchUrl;
  updateURL();

  const url = `${props.searchUrl}?q=*&maxResults=50&resultDetail=All`;
  const request = await fetch(url);
  const response = await request.json();

  data.value = response;
  searchId.value = response.SearchId ?? null;
  industryTags.value = data.value.GroupSummations?.find(p => p.Property === 'IndustryTags')?.Groups ?? [];
  ruleTags.value = data.value.GroupSummations?.find(p => p.Property === 'SubTypeTags')?.Groups ?? [];
};

const fetchData = async () => {
  updateURL();

  let searchUrl = props.searchUrl;
  const isRules = resultType.value === 'rules';

  if (isRules) {
    searchUrl = props.rulesUrl;
  }

  // use '*' for the actual request when input is empty
  const searchQuery = searchString.value.toString().trim() === '' ? '*' : searchString.value;

  const industryTagQuery = selectedIndustryTags.value
      .map(tag => `&IndustryTags=${encodeURIComponent(tag.Value)}`)
      .join('');
  
  // In ankiro they're called typeTags, but here they're referred to as ruleTags
  const ruleTagQuery = selectedRuleTags.value
      .map(tag => `&SubTypeTags=${encodeURIComponent(tag.Value)}`)
      .join('');
  
  const url = `${currentUrl.value}?q=${searchQuery}${industryTagQuery}${ruleTagQuery}&maxResults=${pageSize.value}&resultDetail=All`;
  const request = await fetch(url);
  const response = await request.json(); 
  const bannerObject = response.Decorations.Banners === null ? null : response.Decorations.Banners[0];
  
  if(bannerObject) 
    banner.value = bannerObject;

  data.value = response;
  searchId.value = response.SearchId ?? null;
  industryTags.value = data.value.GroupSummations?.find(p => p.Property === 'IndustryTags')?.Groups ?? [];
  ruleTags.value = data.value.GroupSummations?.find(p => p.Property === 'SubTypeTags')?.Groups ?? [];
}

const selectedIndustryTagValues = computed({
  get() {
    return selectedIndustryTags.value.map(tag => tag.Value);
  },
  set(newValues) {
    selectedIndustryTags.value = newValues.map(val => {
      return industryTags.value.find(tag => tag.Value === val) || { Value: val };
    });
    fetchData();
  }
});

const selectedRuleTagValues = computed({
  get() {
    return selectedRuleTags.value.map(tag => tag.Value);
  },
  set(newValues) {
    selectedRuleTags.value = newValues.map(val => {
      return ruleTags.value.find(tag => tag.Value === val) || { Value: val };
    });
    fetchData();
  }
});

const filters = computed(() => [
  {
    label: 'Branche',
    options: industryTags.value.map(tag => tag.Value),
    model: computed({
      get() {
        return selectedIndustryTags.value.map(t => t.Value);
      },
      set(newValues) {
        selectedIndustryTags.value = newValues.map(val => {
          return industryTags.value.find(tag => tag.Value === val) || { Value: val };
        });
        fetchData();
      }
    })
  },
  {
    label: 'Rule',
    options: ruleTags.value.map(tag => tag.Value),
    model: computed({
      get() {
        return selectedRuleTags.value.map(t => t.Value);
      },
      set(newValues) {
        selectedRuleTags.value = newValues.map(val => {
          return ruleTags.value.find(tag => tag.Value === val) || { Value: val };
        });
        fetchData();
      }
    })
  },
]);

watch(resultType, async (newType, oldType) => {
  if (newType === 'rules') {
    currentUrl.value = props.rulesUrl;
  } else {
    currentUrl.value = props.searchUrl;

    resetSearch();
  }

  await fetchData();
});
</script>